// Shared by the website (React) and the build-time page generator (scripts/prerender.mjs), so the HTML that
// search engines and AI assistants read is built from exactly the same rules as the page visitors see.
// Plain JavaScript with no imports: Node runs it directly at build time.

export const BRAND = "Elegant Home Advisors";

/** "Navi Mumbai" -> "navi-mumbai". The area page of a locality is /flats-in-<slug>. */
export const localitySlug = (name) => String(name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

/** The area a listing belongs to: "Kharghar, Sector 34A" -> "Kharghar". */
export const areaName = (locality) => String(locality || "").split(",")[0].trim();
export const areaSlug = (locality) => localitySlug(areaName(locality));
export const areaPath = (locality) => `/flats-in-${areaSlug(locality)}`;

/** "₹1.25 Cr onwards" -> 12500000, "₹63.99 Lakh + taxes" -> 6399000, "₹37 L" -> 3700000. 0 when unknown. */
export function parsePrice(text) {
    const s = String(text || "").replace(/,/g, "").toLowerCase();
    const m = s.match(/(\d+(?:\.\d+)?)(?:\s*(?:-|–|to)\s*\d+(?:\.\d+)?)?\s*(cr|crore|crores|l|lac|lacs|lakh|lakhs|k)?\b/);
    if (!m) return 0;
    const n = parseFloat(m[1]);
    const unit = m[2] || "";
    if (unit.startsWith("cr")) return Math.round(n * 1e7);
    if (unit.startsWith("l")) return Math.round(n * 1e5);
    if (unit === "k") return Math.round(n * 1e3);
    return n >= 100000 ? Math.round(n) : 0;   // a bare small number is not a price
}

export const formatINR = (n) => (n ? `₹${Math.round(n).toLocaleString("en-IN")}` : "");

export function formatPriceShort(n) {
    if (!n) return "";
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2).replace(/\.?0+$/, "")} Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2).replace(/\.?0+$/, "")} Lakh`;
    return formatINR(n);
}

// Only RERA carpet area is compared; "carpet + balcony", built-up and super built-up areas are left out.
const isCarpet = (plan) => !/balcony|built|super|saleable|usable/i.test(`${plan.area_type || ""} ${plan.area || ""}`);

/** "388–579 sq ft": the RERA carpet areas of a listing's floor plans (or its headline carpet area). */
export function carpetRange(p) {
    const areas = (p?.floor_plans || []).filter(isCarpet).map((f) => Number(f.carpet_area_sqft)).filter(Boolean);
    if (areas.length) { const a = Math.min(...areas), b = Math.max(...areas); return `${a}${b > a ? `–${b}` : ""} sq ft`; }
    return p?.carpet_area ? `${p.carpet_area} sq ft` : "";
}

/** Page copy shared by the app and the prerendered HTML, so both say the same thing. */
export const EMI_COPY = { h1: "Home Loan EMI Calculator", tagline: "Plan your EMI before you fall in love with the home." };
export const CONTACT_COPY = {
    h1: "Contact a Property Advisor in Navi Mumbai",
    intro: "Call, WhatsApp or write to us for a free shortlist of RERA-registered projects, a guided site visit or help with home-loan paperwork. We typically respond within 30 minutes during business hours.",
    tellHeading: "What to tell us",
    tell: [
        "Your budget, including how much you plan to borrow",
        "The areas you prefer: Kharghar, Taloja, Panvel or elsewhere in Navi Mumbai",
        "The home you need: 1, 2, 3 or 4 BHK",
        "When you want to move in: ready to move or under construction",
    ],
    nextHeading: "What happens next",
    next: [
        "An advisor calls or messages you to understand what you are looking for.",
        "We send a shortlist of RERA-registered projects that fit, with prices, carpet areas and MahaRERA numbers.",
        "We arrange free guided site visits and help you compare offers, paperwork and the home loan.",
    ],
};
/** "+91 91524 00737" from the WhatsApp digits "919152400737". */
export const phoneDisplay = (digits) => {
    const d = String(digits || "").replace(/\D/g, "");
    return d.length === 12 && d.startsWith("91") ? `+91 ${d.slice(2, 7)} ${d.slice(7)}` : d ? `+${d}` : "";
};
/** The map to show on /contact: the embed link from Admin, or one made from a full street address. */
export const contactMapSrc = (s) => {
    if (/^https:\/\/www\.google\.com\/maps\/embed/.test(s?.map_embed_url || "")) return s.map_embed_url;
    return s?.address_street ? `https://www.google.com/maps?q=${encodeURIComponent(addressLine(s))}&output=embed` : "";
};

/** The homepage photo when none is uploaded in Admin -> Settings. */
export const DEFAULT_HERO = "https://images.pexels.com/photos/7031594/pexels-photo-7031594.jpeg";
export const HERO_WIDTHS = [640, 960, 1280, 1920];

/** Image alt text, the same in the app and the prerendered HTML. */
export const LOGO_ALT = "Elegant Home Advisors logo";
export const photoAlt = (name, i, n) => `${name} – photo ${i + 1} of ${n}`;
/** "Orelia 2 BHK floor plan, 683.72 sq ft" (the size is left out when the plan has none). */
export function floorPlanAlt(name, f) {
    const m = String(f?.area || "").match(/[\d,]+(?:\.\d+)?/);
    const sqft = Number(f?.carpet_area_sqft) || (m ? Number(m[0].replace(/,/g, "")) : 0);
    const size = sqft ? `, ${sqft.toLocaleString("en-IN", { maximumFractionDigits: 2 })} sq ft` : "";
    return `${[name, f?.config].filter(Boolean).join(" ")} floor plan${size}`;
}

/** Price per sq ft (carpet) of one floor plan, or 0 when the plan has no usable price or carpet area. */
export function planPsf(plan) {
    const area = Number(plan?.carpet_area_sqft) || 0;
    const price = parsePrice(plan?.price);
    if (!area || !price || !isCarpet(plan)) return 0;
    const psf = price / area;
    return psf >= 2000 && psf <= 200000 ? Math.round(psf) : 0;   // ignore obviously wrong data
}

/** {min, max} price per sq ft across a property's floor plans, or null. */
export function propertyPsf(p) {
    const vals = (p?.floor_plans || []).map(planPsf).filter(Boolean);
    if (!vals.length) return null;
    return { min: Math.min(...vals), max: Math.max(...vals) };
}

const median = (arr) => {
    if (!arr.length) return 0;
    const s = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return Math.round(s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2);
};

export function priceOf(p) {
    return Number(p?.price_min) || parsePrice(p?.starting_price) || 0;
}

/** Facts about a set of listings (an area page), all computed from the listings themselves. */
export function areaStats(props) {
    const prices = props.map(priceOf).filter(Boolean);
    const maxes = props.map((p) => Number(p.price_max) || priceOf(p)).filter(Boolean);
    const psfs = props.map((p) => propertyPsf(p)?.min).filter(Boolean);
    const allPlanPsf = props.flatMap((p) => (p.floor_plans || []).map(planPsf)).filter(Boolean);
    const areas = props.flatMap((p) => [Number(p.carpet_area) || 0, ...(p.floor_plans || []).filter(isCarpet).map((f) => Number(f.carpet_area_sqft) || 0)]).filter(Boolean);
    const rank = (c) => (/studio/i.test(c) ? 0 : parseFloat(c) || 99);
    const configs = [...new Set(props.flatMap((p) => p.configurations || []))].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
    return {
        count: props.length,
        minPrice: prices.length ? Math.min(...prices) : 0,
        maxPrice: maxes.length ? Math.max(...maxes) : 0,
        psfMedian: median(psfs),
        psfMin: allPlanPsf.length ? Math.min(...allPlanPsf) : 0,
        psfMax: allPlanPsf.length ? Math.max(...allPlanPsf) : 0,
        psfCount: psfs.length,
        minArea: areas.length ? Math.min(...areas) : 0,
        maxArea: areas.length ? Math.max(...areas) : 0,
        configs,
        ready: props.filter((p) => p.possession_status === "Ready to Move" || p.category === "Ready to Move"),
        builders: [...new Set(props.map((p) => p.builder).filter(Boolean))],
    };
}

/** How a property's price per sq ft compares with the other listings in its area (needs 2+ with prices). */
export function psfComparison(p, areaProps) {
    const own = propertyPsf(p);
    if (!own) return null;
    const others = areaProps.filter((x) => x.slug !== p.slug).map((x) => propertyPsf(x)?.min).filter(Boolean);
    if (!others.length) return { own, areaMedian: 0, diffPct: null, sample: 0 };
    const areaMedian = median([own.min, ...others]);
    return { own, areaMedian, diffPct: Math.round(((own.min - areaMedian) / areaMedian) * 100), sample: others.length + 1 };
}

export const placeOf = (p) => [p.locality, p.location, p.city].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(", ");

export function propertyDescription(p) {
    return p.seo_description || p.short_description || (p.description || "").slice(0, 155) || `${p.name} by ${p.builder} in ${p.location}.`;
}

export function propertyTitle(p) {
    return p.seo_title || `${p.name} by ${p.builder} | ${[p.locality, p.location].filter(Boolean).join(", ")} | ${BRAND}`;
}

export const propertyGallery = (p) => [p.cover_image, ...(p.images || [])].filter((u, i, a) => u && a.indexOf(u) === i);

export function reraList(p) {
    const list = (p.rera_entries || []).filter((e) => e && (e.number || e.qr_url));
    if (list.length) return list;
    return p.rera_number ? [{ label: "", number: p.rera_number, qr_url: p.rera_qr_url || "" }] : [];
}

/** Home > Properties > Flats in <Area> > <name> */
export function propertyCrumbs(p) {
    const crumbs = [{ name: "Home", path: "/" }, { name: "Properties", path: "/properties" }];
    if (areaSlug(p.locality)) crumbs.push({ name: `Flats in ${areaName(p.locality)}`, path: areaPath(p.locality) });
    crumbs.push({ name: p.name, path: `/property/${p.slug}` });
    return crumbs;
}

export function breadcrumbLd(crumbs, site, id) {
    return {
        "@type": "BreadcrumbList", ...(id ? { "@id": id } : {}),
        itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: `${site}${c.path}` })),
    };
}

/** Structured data for a property page: only real fields, no ratings or reviews. */
export function propertyJsonLd(p, site, images) {
    const url = `${site}/property/${p.slug}`;
    const description = propertyDescription(p);
    const price = priceOf(p);
    const rera = reraList(p).map((r) => r.number).filter(Boolean);
    const lat = Number(p.latitude), lng = Number(p.longitude);
    const address = {
        "@type": "PostalAddress",
        ...(p.address ? { streetAddress: p.address } : {}),
        addressLocality: p.locality || p.location || undefined,
        addressRegion: "Maharashtra", addressCountry: "IN",
    };
    const residence = {
        "@type": "ApartmentComplex", "@id": `${url}#place`, name: p.name, address,
        ...(lat && lng ? { geo: { "@type": "GeoCoordinates", latitude: lat, longitude: lng } } : {}),
        ...(p.amenities?.length ? { amenityFeature: p.amenities.slice(0, 30).map((a) => ({ "@type": "LocationFeatureSpecification", name: a, value: true })) } : {}),
    };
    const offers = (p.floor_plans || []).map((f) => ({ f, price: parsePrice(f.price) })).filter((x) => x.price > 0);
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "RealEstateListing", "@id": `${url}#listing`, url, name: p.name, description,
                image: images, datePosted: p.created_at, dateModified: p.updated_at, inLanguage: "en-IN",
                breadcrumb: { "@id": `${url}#breadcrumb` }, about: { "@id": `${url}#place` },
                ...(price > 0 ? {
                    offers: offers.length > 1
                        ? { "@type": "AggregateOffer", priceCurrency: "INR", lowPrice: Math.min(...offers.map((o) => o.price)), highPrice: Math.max(...offers.map((o) => o.price)), offerCount: offers.length, availability: p.badge === "Sold Out" ? "https://schema.org/SoldOut" : "https://schema.org/InStock" }
                        : { "@type": "Offer", url, priceCurrency: "INR", price, availability: p.badge === "Sold Out" ? "https://schema.org/SoldOut" : "https://schema.org/InStock" },
                } : {}),
                ...(rera.length ? { identifier: rera.map((n) => ({ "@type": "PropertyValue", propertyID: "MahaRERA", value: n })) } : {}),
                provider: { "@type": "RealEstateAgent", name: BRAND, url: site },
            },
            residence,
            breadcrumbLd(propertyCrumbs(p), site, `${url}#breadcrumb`),
        ],
    };
}

export function itemListLd(props, site, name, url) {
    return {
        "@type": "ItemList", name, url,
        numberOfItems: props.length,
        itemListElement: props.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${site}/property/${p.slug}`, name: p.name })),
    };
}

export function faqLd(qas) {
    const list = (qas || []).filter((q) => q && q.question && q.answer);
    if (!list.length) return null;
    return {
        "@type": "FAQPage",
        mainEntity: list.map((q) => ({ "@type": "Question", name: q.question, acceptedAnswer: { "@type": "Answer", text: stripMarkdown(q.answer) } })),
    };
}

/** The areas the agency covers (schema.org areaServed). */
export const AREAS_SERVED = ["Kharghar", "Taloja", "Panvel", "Navi Mumbai"];
/** Raster logo for structured data (Google wants PNG/JPG, at least 112×112). */
export const LOGO_PNG = "/brand/logo-512.png";
export const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** Office hours grouped by identical times: [{days: ["Monday", ...], opens, closes}], in week order. */
export function hoursGroups(hours) {
    const out = [];
    for (const d of DAYS) {
        const h = hours?.[d];
        if (!h?.opens || !h?.closes) continue;
        const last = out[out.length - 1];
        if (last && last.opens === h.opens && last.closes === h.closes && DAYS.indexOf(last.days[last.days.length - 1]) === DAYS.indexOf(d) - 1) last.days.push(d);
        else out.push({ days: [d], opens: h.opens, closes: h.closes });
    }
    return out;
}
const time12 = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
/** ["Monday – Saturday: 10:00 AM – 7:00 PM", "Sunday: closed"] for the Contact page. */
export function hoursLines(hours) {
    const groups = hoursGroups(hours);
    if (!groups.length) return [];
    const open = new Set(groups.flatMap((g) => g.days));
    const closed = DAYS.filter((d) => !open.has(d));
    return [
        ...groups.map((g) => `${g.days.length > 1 ? `${g.days[0]} – ${g.days[g.days.length - 1]}` : g.days[0]}: ${time12(g.opens)} – ${time12(g.closes)}`),
        ...(closed.length ? [`${closed.join(", ")}: closed`] : []),
    ];
}
/** The office address as one line, from the separate fields (falls back to the free-text address). */
export function addressLine(s) {
    const parts = [s?.address_street, s?.address_locality, s?.address_city, s?.address_postal_code ? `Maharashtra ${s.address_postal_code}` : ""].filter(Boolean);
    return s?.address_street ? parts.join(", ") : (s?.address || parts.join(", "));
}

/** schema.org RealEstateAgent. Address parts, geo and hours are included only when set in Admin → Settings. */
export function agentLd(settings, site) {
    const s = settings || {};
    const logo = `${site}${LOGO_PNG}`;
    const locality = s.address_locality || s.address_city || "";
    const hours = hoursGroups(s.opening_hours);
    return {
        "@type": "RealEstateAgent", "@id": `${site}/#agent`, name: BRAND, url: site,
        logo: { "@type": "ImageObject", url: logo, width: 512, height: 512 }, image: logo,
        ...(s.phone ? { telephone: s.phone } : {}),
        ...(s.email ? { email: s.email } : {}),
        address: {
            "@type": "PostalAddress",
            ...(s.address_street ? { streetAddress: s.address_street } : {}),
            ...(locality ? { addressLocality: locality } : {}),
            ...(s.address_postal_code ? { postalCode: s.address_postal_code } : {}),
            addressRegion: "Maharashtra", addressCountry: "IN",
        },
        ...(s.geo_lat && s.geo_lng ? { geo: { "@type": "GeoCoordinates", latitude: Number(s.geo_lat), longitude: Number(s.geo_lng) } } : {}),
        ...(hours.length ? { openingHoursSpecification: hours.map((h) => ({ "@type": "OpeningHoursSpecification", dayOfWeek: h.days.map((d) => `https://schema.org/${d}`), opens: h.opens, closes: h.closes })) } : {}),
        areaServed: AREAS_SERVED.map((name) => ({ "@type": name === "Navi Mumbai" ? "City" : "Place", name })),
        ...(s.maharera_number ? { identifier: { "@type": "PropertyValue", propertyID: "MahaRERA agent registration", value: s.maharera_number } } : {}),
        sameAs: [s.facebook, s.instagram, s.linkedin, s.youtube].filter(Boolean),
    };
}

/** schema.org for /contact: a ContactPage about the agency. */
export function contactLd(settings, site) {
    return {
        "@type": "ContactPage", "@id": `${site}/contact#page`, url: `${site}/contact`, name: PAGES.contact.title,
        description: PAGES.contact.description, mainEntity: agentLd(settings, site),
    };
}

/** schema.org for /emi-calculator: a free web app (FinanceApplication). */
export function emiAppLd(site) {
    return {
        "@type": "WebApplication", "@id": `${site}/emi-calculator#app`, name: "Home Loan EMI Calculator", url: `${site}/emi-calculator`,
        description: PAGES.emi.description, applicationCategory: "FinanceApplication", operatingSystem: "Any",
        browserRequirements: "Requires JavaScript", isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
        provider: { "@id": `${site}/#agent` },
    };
}

/** The people on the About page with at least a name, in the order the admin set. */
export const aboutPeople = (about) => ((about && about.people) || []).filter((p) => p && String(p.name || "").trim());

/** schema.org for /about: an AboutPage about the agency, with its founder and advisors as people. */
export function aboutLd(settings, site) {
    const s = settings || {};
    const a = s.about || {};
    const people = aboutPeople(a);
    const person = (p) => ({
        "@type": "Person", name: p.name.trim(),
        ...(p.role ? { jobTitle: p.role } : {}),
        ...(p.photo ? { image: p.photo } : {}),
        ...(p.bio ? { description: stripMarkdown(p.bio).slice(0, 500) } : {}),
        ...(p.languages ? { knowsLanguage: p.languages.split(/[,/]/).map((x) => x.trim()).filter(Boolean) } : {}),
        ...(p.linkedin ? { sameAs: [p.linkedin] } : {}),
        worksFor: { "@id": `${site}/#agent` },
    });
    const founders = people.filter((p) => /founder/i.test(p.role || ""));
    return {
        "@type": "AboutPage", "@id": `${site}/about#page`, url: `${site}/about`, name: PAGES.about.title,
        mainEntity: {
            ...agentLd(s, site),
            ...(a.founded ? { foundingDate: a.founded } : {}),
            ...(founders.length ? { founder: founders.map(person) } : {}),
            ...(people.length ? { employee: people.map(person) } : {}),
        },
    };
}

export function articleLd(a, site) {
    const url = `${site}/guides/${a.slug}`;
    return {
        "@type": "Article", "@id": `${url}#article`, headline: a.title, description: a.summary || "",
        url, mainEntityOfPage: url, inLanguage: "en-IN",
        datePublished: a.created_at, dateModified: a.updated_at || a.created_at,
        ...(a.cover_image ? { image: [a.cover_image] } : {}),
        author: { "@type": "Organization", name: BRAND, url: site },
        publisher: { "@type": "Organization", name: BRAND, url: site },
    };
}

export const graph = (...nodes) => ({ "@context": "https://schema.org", "@graph": nodes.filter(Boolean) });

/** Questions and answers for an area page, written from the listings themselves. */
export function areaFaqs(name, props, stats) {
    if (!props.length) return [];
    const list = [];
    const names = props.map((p) => `${p.name} (${p.builder})`).join(", ");
    list.push({
        question: `Which new projects are available in ${name}?`,
        answer: `${BRAND} currently lists ${props.length} RERA-registered ${props.length === 1 ? "project" : "projects"} in ${name}: ${names}.`,
    });
    if (stats.minPrice) {
        list.push({
            question: `What is the price of flats in ${name}?`,
            answer: `Listed prices in ${name} start from ${formatPriceShort(stats.minPrice)}${stats.maxPrice > stats.minPrice ? ` and go up to about ${formatPriceShort(stats.maxPrice)}` : ""}${stats.configs.length ? ` for ${stats.configs.join(", ")} homes` : ""}. Final prices depend on the floor, view and the developer's current price list.`,
        });
    }
    if (stats.psfMedian && stats.psfCount >= 2) {
        list.push({
            question: `What is the average price per sq ft in ${name}?`,
            answer: `Across the ${stats.psfCount} ${name} projects we list with published floor-plan prices, the typical starting rate is about ${formatINR(stats.psfMedian)} per sq ft of RERA carpet area (range ${formatINR(stats.psfMin)}–${formatINR(stats.psfMax)}).`,
        });
    }
    list.push({
        question: `Are there ready-to-move flats in ${name}?`,
        answer: stats.ready.length
            ? `Yes. ${stats.ready.map((p) => p.name).join(", ")} ${stats.ready.length === 1 ? "is" : "are"} listed as ready to move. Ask us to confirm the occupancy certificate (OC) for the tower you like.`
            : `Not at the moment: the ${name} projects we list are under construction. We can tell you the RERA completion date of each.`,
    });
    return list;
}

// ---------- Markdown (a small, safe subset for buyer guides) ----------
const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
export const escapeHtml = esc;

function inline(text) {
    let s = esc(text);
    s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    s = s.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
    s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, href) => {
        const h = href.replace(/&amp;/g, "&");
        if (!/^(https?:\/\/|\/|#|mailto:|tel:)/i.test(h)) return label;
        const ext = /^https?:\/\//i.test(h);
        return `<a href="${esc(h)}"${ext ? ' target="_blank" rel="noopener noreferrer"' : ""}>${label}</a>`;
    });
    return s;
}

/** ## / ### headings, paragraphs, - and 1. lists, > notes, | tables |, **bold**, *italic*, [links](/path). Escaped. */
export function renderMarkdown(md) {
    const lines = String(md || "").replace(/\r\n?/g, "\n").split("\n");
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const t = line.trim();
        if (!t) { i++; continue; }
        let m;
        if ((m = t.match(/^(#{2,4})\s+(.*)$/))) {
            const lvl = m[1].length;
            const id = localitySlug(m[2]);
            out.push(`<h${lvl} id="${id}">${inline(m[2])}</h${lvl}>`);
            i++;
        } else if (/^[-*]\s+/.test(t)) {
            const items = [];
            while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) { items.push(`<li>${inline(lines[i].trim().replace(/^[-*]\s+/, ""))}</li>`); i++; }
            out.push(`<ul>${items.join("")}</ul>`);
        } else if (/^\d+[.)]\s+/.test(t)) {
            const items = [];
            while (i < lines.length && /^\d+[.)]\s+/.test(lines[i].trim())) { items.push(`<li>${inline(lines[i].trim().replace(/^\d+[.)]\s+/, ""))}</li>`); i++; }
            out.push(`<ol>${items.join("")}</ol>`);
        } else if (t.startsWith(">")) {
            const parts = [];
            while (i < lines.length && lines[i].trim().startsWith(">")) { parts.push(lines[i].trim().replace(/^>\s?/, "")); i++; }
            out.push(`<blockquote><p>${inline(parts.join(" "))}</p></blockquote>`);
        } else if (t.startsWith("|")) {
            const rows = [];
            while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(lines[i].trim()); i++; }
            const cells = (r) => r.replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
            const body = rows.filter((r) => !/^\|?\s*:?-{2,}/.test(r.replace(/^\|/, "")));
            const [head, ...rest] = body;
            out.push(`<div class="table-wrap"><table><thead><tr>${cells(head).map((c) => `<th>${inline(c)}</th>`).join("")}</tr></thead><tbody>${rest.map((r) => `<tr>${cells(r).map((c) => `<td>${inline(c)}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`);
        } else {
            const parts = [];
            while (i < lines.length && lines[i].trim() && !/^(#{2,4}\s|[-*]\s|\d+[.)]\s|>|\|)/.test(lines[i].trim())) { parts.push(lines[i].trim()); i++; }
            out.push(`<p>${inline(parts.join(" "))}</p>`);
        }
    }
    return out.join("\n");
}

export function stripMarkdown(md) {
    return String(md || "")
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/\*\*?([^*]+)\*\*?/g, "$1")
        .replace(/^#{1,6}\s+/gm, "").replace(/^[-*>]\s+/gm, "").replace(/\s+/g, " ").trim();
}

/** Headings of a guide, for its table of contents. */
export const markdownHeadings = (md) => String(md || "").split("\n").map((l) => l.trim().match(/^##\s+(.*)$/)).filter(Boolean).map((m) => ({ id: localitySlug(m[1]), text: m[1] }));

/** Titles and descriptions of the fixed pages, used by the pages themselves and by the page generator. */
export const PAGES = {
    home: {
        path: "/",
        title: `${BRAND} | RERA Projects & Flats in Navi Mumbai`,
        description: "RERA-registered new projects and flats in Kharghar, Taloja and Panvel, Navi Mumbai. Compare prices, floor plans and possession, and book a free site visit.",
    },
    properties: {
        path: "/properties",
        title: `All Properties: New Projects & Ready Flats | ${BRAND}`,
        description: "Every RERA-registered project we list, with prices, carpet areas, floor plans, possession dates and RERA numbers. Filter by area, budget and BHK.",
    },
    guides: {
        path: "/guides",
        title: `Home Buyer Guides for Navi Mumbai | ${BRAND}`,
        description: "Plain-English guides for home buyers: carpet vs built-up area, OC vs CC, Jodi flats, checking a project on MahaRERA, and stamp duty and registration costs.",
    },
    about: {
        path: "/about",
        title: `About Us: Founder & Property Advisors | ${BRAND}`,
        description: "Who we are: Elegant Home Advisors helps buyers choose RERA-registered new projects and ready flats in Navi Mumbai. Meet our founder and advisors.",
    },
    contact: {
        path: "/contact",
        title: `Contact a Property Advisor | ${BRAND}`,
        description: "Call, WhatsApp or write to Elegant Home Advisors for a free shortlist, site visit or home-loan help in Navi Mumbai.",
    },
    emi: {
        path: "/emi-calculator",
        title: `Home Loan EMI Calculator | ${BRAND}`,
        description: "Work out your monthly home-loan EMI, total interest and repayment schedule for any property price, down payment, interest rate and tenure.",
    },
    privacy: { path: "/privacy-policy", title: `Privacy Policy | ${BRAND}`, description: "How Elegant Home Advisors collects, uses and protects the details you share with us." },
    terms: { path: "/terms-of-service", title: `Terms of Service | ${BRAND}`, description: "The terms for using the Elegant Home Advisors website." },
    compare: { path: "/compare", title: `Compare Properties | ${BRAND}`, description: "Compare price, size, location and amenities of the properties you are considering." },
    shortlist: { path: "/shortlist", title: `My Shortlisted Properties | ${BRAND}`, description: "Properties you have saved." },
    notFound: { path: "/404", title: `Page not found | ${BRAND}`, description: "This page doesn't exist or has moved." },
};

export const areaTitle = (name) => `New Projects & Flats in ${name} | Prices & RERA | ${BRAND}`;
export function areaDescription(name, stats) {
    const bits = [`${stats.count} RERA-registered ${stats.count === 1 ? "project" : "projects"} in ${name}`];
    if (stats.configs.length) bits.push(stats.configs.join(", "));
    if (stats.minPrice) bits.push(`from ${formatPriceShort(stats.minPrice)}`);
    return `${bits.join(" · ")}. Compare price per sq ft, floor plans and possession dates, and book a free site visit.`;
}
export const guideTitle = (a) => a.seo_title || `${a.title} | ${BRAND}`;
export const guideDescription = (a) => a.seo_description || a.summary || stripMarkdown(a.body).slice(0, 155);
